import React, { useState } from "react";
import Link from "next/link";
import {
  css,
  Grid,
  Heading,
  Icon,
  PrimaryButton,
  styled,
  Link as SourcefulLink,
} from "@sourceful/shared-components";
import { SanitySlidesSection, SlidesSectionLayout } from "@sourceful/shared-types";
import { getSanityImgUrl } from "@lib/sanity";
import { BlockContent } from "@lib/sanity-components";
import toRem from "@styles/postprocess/conversion/to-rem";
import { CTAGroup } from "@components/CTAGroup";
import useInterval from "@lib/useInterval";
import { PageSectionWrapper, nestedOverrides } from "../BasePageSection";
import { IMAGE_DIMENSION_SLIDES_SECTION_WIDTH } from "@lib/utils/imageDimensions";

const Content = styled("div", {
  width: "100%",
  display: "grid",
  gridTemplateColumns: "1fr",
  "@lg": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const TextPanel = styled("div", {
  gridRow: "1",
  paddingBottom: "clamp(40px, 4vw, 77px)",
  "@lg": {
    gridRow: "1",
    paddingBottom: 0,
  },
  variants: {
    placement: {
      left: {
        "@lg": {
          gridColumn: "1",
          paddingRight: "clamp(10px, 4vw, 77px)",
        },
      },
      right: {
        "@lg": {
          gridColumn: "2",
          paddingLeft: "clamp(10px, 4vw, 77px)",
        },
      },
    },
  },
});

const ImagePanel = styled("div", {
  marginTop: toRem(20),
  gridRow: "2",
  display: "grid",
  gridTemplateRows: "max-content max-content",
  justifyContent: "center",
  gridGap: toRem(20),
  "@lg": {
    gridRow: "1",
  },
  variants: {
    placement: {
      left: {
        "@lg": {
          gridColumn: "1",
        },
      },
      right: {
        "@lg": {
          gridColumn: "2",
        },
      },
    },
  },
});

const StyledImg = styled("img", {
  width: "100%",
  maxWidth: toRem(650),
  variants: {
    placement: {
      left: {},
      right: {},
    },
  },
});

const ImageButton = styled("button", {
  background: "none",
  border: "none",
  fontFamily: "inherit",
  color: "$neutral_solid_100",
  fontSize: "$p2",
  cursor: "pointer",
  borderRadius: "$medium",
  transition: "all 0.2s",
  height: toRem(42),
  textAlign: "left",
  variants: {
    active: {
      true: {
        background: "#F6F6F6",
      },
    },
  },
});

const CarouselControlBar = styled("div", {
  display: "flex",
  margin: "0 auto",
});

const CarouselButton = styled("button", {
  background: "none",
  borderRadius: "$round",
  height: "20px",
  width: "20px",
  border: "2px solid transparent",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginLeft: toRem(5),
  cursor: "pointer",
  "&:first-child": {
    marginLeft: 0,
  },
  "&::after": {
    content: " ",
    display: "block",
    width: toRem(6),
    height: toRem(4),
    background: "#979799",
    borderRadius: "$round",
  },

  variants: {
    active: {
      true: {
        border: "2px solid $palette1_colour1",
        "&::after": {
          content: " ",
          display: "block",
          width: toRem(6),
          height: toRem(4),
          background: "$palette1_colour1",
          borderRadius: "$round",
        },
      },
    },
  },
});

const ctaGroupStyles = css({
  marginTop: "clamp(30px, 5vw, 60px)",
  justifyContent: "center",
  "@lg": {
    justifyContent: "flex-start",
  },
});

const iconStyles = css({
  marginBottom: toRem(10),
});

interface SlidesSectionProps {
  maxWidth?: string;
  className?: string;
  section: SanitySlidesSection;
}

export const SlidesSection = ({ section, maxWidth = "1200px", className }: SlidesSectionProps) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const {
    _key,
    icon,
    title,
    subtitle,
    primaryCTA,
    secondaryCTA,
    linkCTA,
    backgroundColour,
    textColour,
    constrainContent,
    images,
    layout,
    nested,
  } = section;

  const galleryId = `${title}-gallery`;

  const currentImage = images && images.length > 0 ? images[selectedPhotoIndex] : null;

  useInterval(() => {
    if (!images || images.length === 0) {
      return;
    }

    setSelectedPhotoIndex(current => {
      if (current === images.length - 1) return 0;
      return current + 1;
    });
  }, 3000);

  return (
    <PageSectionWrapper
      css={{
        backgroundColor: backgroundColour?.value || "auto",
        ...nestedOverrides(nested || false),
      }}
      className={className}
      id={`s${_key}`}
      aria-labelledby={`sh${_key}`}
    >
      <Content css={{ maxWidth: constrainContent ? maxWidth : "100%" }}>
        <TextPanel placement={layout === SlidesSectionLayout.TEXT_PANEL_LEFT ? "left" : "right"}>
          {icon && <Icon name={icon} width={33} height={33} className={iconStyles()} />}
          <Heading
            level={2}
            fontSize={3}
            css={{ color: textColour?.value || "$neutral_solid_100" }}
          >
            {title}
          </Heading>

          {subtitle && <BlockContent value={subtitle} color={textColour?.value} />}

          {images && images.length > 1 && (
            <Grid
              gridGap="5px"
              css={{ marginTop: "clamp(30px, 5vw, 60px)", "@lg": { maxWidth: toRem(354) } }}
            >
              {images?.map((img, index) => {
                return (
                  <ImageButton
                    key={img._key}
                    active={index === selectedPhotoIndex}
                    onClick={() => setSelectedPhotoIndex(index)}
                    css={{ color: textColour?.value || "$neutral_solid_100" }}
                  >
                    {img?.metadata?.caption}
                  </ImageButton>
                );
              })}
            </Grid>
          )}

          {primaryCTA && primaryCTA?.url && (
            <CTAGroup className={ctaGroupStyles()}>
              <Link passHref href={primaryCTA.url} legacyBehavior>
                <PrimaryButton as="a" compact tabIndex={0}>
                  {primaryCTA?.title}
                </PrimaryButton>
              </Link>
              {secondaryCTA && secondaryCTA?.title && secondaryCTA?.url && (
                <Link passHref href={secondaryCTA.url} legacyBehavior>
                  <PrimaryButton as="a" appearance="light" compact tabIndex={0}>
                    {secondaryCTA.title}
                  </PrimaryButton>
                </Link>
              )}
              {linkCTA && linkCTA?.title && linkCTA?.url && (
                <Link passHref href={linkCTA.url} legacyBehavior>
                  <SourcefulLink href={linkCTA.url}>{linkCTA.title}</SourcefulLink>
                </Link>
              )}
            </CTAGroup>
          )}
        </TextPanel>

        <ImagePanel placement={layout === SlidesSectionLayout.TEXT_PANEL_LEFT ? "right" : "left"}>
          <StyledImg
            id={galleryId}
            loading="lazy"
            src={
              currentImage
                ? getSanityImgUrl(currentImage.asset._ref, base =>
                    base.width(IMAGE_DIMENSION_SLIDES_SECTION_WIDTH)
                  )
                : ""
            }
            alt={currentImage?.asset?.altText}
          />
          <CarouselControlBar>
            {images &&
              images.length > 1 &&
              images?.map((img, index) => {
                return (
                  <CarouselButton
                    key={img.asset._ref}
                    active={index === selectedPhotoIndex}
                    aria-label={`show-image-${img?.asset?.altText}`}
                    onClick={() => setSelectedPhotoIndex(index)}
                    aria-controls={galleryId}
                  />
                );
              })}
          </CarouselControlBar>
        </ImagePanel>
      </Content>
    </PageSectionWrapper>
  );
};

export default SlidesSection;
